import { useContext, useEffect, useState } from "react";
import { NavLink, useMatch, useNavigate } from "react-router";

import { ApiContext } from "../../api-context";

import { createNavigationItems } from "./config";

import type { Session, SetUserSession } from "../../session";

export type NavigationProps = {
  readonly userSession: Session;
  readonly setUserSession: SetUserSession;
};

export type NavigationIconProps = {
  showNavigation: boolean;
  setShowNavigation: (show: boolean) => void;
};

export type NavigationItem =
  | ParentNavigationItem
  | LinkNavigationItem
  | ActionNavigationItem;

export type ParentNavigationItem = {
  readonly itemType: "parent";
  readonly basePath: string;
  readonly description: string;
  readonly items: NavigationItem[];
};

export type LinkNavigationItem = {
  readonly itemType: "link";
  readonly path: string;
  readonly description: string;
  readonly authorizedRoles: string[];
};

export type ActionNavigationItem = {
  readonly itemType: "action";
  readonly description: string;
  readonly action: () => Promise<void>;
  readonly authorizedRoles: string[];
};

type LevelProp = {
  readonly level: number;
  readonly parentPath: string;
};

const NavigationItem: React.FC<NavigationItem & LevelProp> = (
  item: NavigationItem & LevelProp,
) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const match = useMatch(`${item.parentPath}*`);

  useEffect(() => {
    if (match) {
      setIsExpanded(true);
    }
  }, [match]);

  switch (item.itemType) {
    case "action": {
      const { description, action } = item;

      return (
        <li>
          <div
            className="item"
            onClick={() => {
              action();
            }}
          >
            <div className="caption border border-0">
              <i className="bi bi-chevron-right"></i>
              <span>{description}</span>
            </div>
          </div>
        </li>
      );
    }
    case "link": {
      const { description, path, parentPath } = item;

      return (
        <li>
          <NavLink to={parentPath + path} className="item">
            <div className="caption">
              <i className="bi bi-chevron-right"></i>
              <span className="fs-sm">{description}</span>
            </div>
          </NavLink>
        </li>
      );
    }
    case "parent": {
      const { description, level, items, basePath, parentPath } = item;

      return (
        <li>
          <div
            className="item"
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
          >
            <div className="caption">
              <i className="bi bi-chevron-right"></i>
              <span>{description}</span>
            </div>
            <i
              className={`bi bi-arrow-${isExpanded ? "up" : "down"}-short`}
            ></i>
          </div>
          {isExpanded ? (
            <ol className={`l${level}`}>
              {items.map((item) => (
                <NavigationItem
                  key={description}
                  level={level + 1}
                  parentPath={parentPath + basePath}
                  {...item}
                />
              ))}
            </ol>
          ) : null}
        </li>
      );
    }
  }
};

export const NavigationIcon: React.FC<NavigationIconProps> = ({
  showNavigation,
  setShowNavigation,
}: NavigationIconProps) => {
  return (
    <div
      className="nav-icon"
      onClick={() => setShowNavigation(!showNavigation)}
    >
      <span>
        <i className="bi bi-list" style={{ fontSize: "28px" }}></i>
      </span>
    </div>
  );
};

export const Navigation: React.FC<NavigationProps> = ({
  userSession,
  setUserSession,
}: NavigationProps) => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();
  const { items, basePath } = createNavigationItems({
    apiFetch,
    userSession,
    setUserSession,
    navigate,
  });

  return (
    <nav className="nav page">
      <ol className="l0">
        {items.map((item) => (
          <NavigationItem
            key={item.description}
            level={1}
            parentPath={basePath}
            {...item}
          />
        ))}
      </ol>
    </nav>
  );
};
